import React from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ReadModeToggle from "@/components/ReadModeToggle";
import AgeBandPicker from "@/components/layout/AgeBandPicker";
import MoreLiveToday from "@/components/layout/MoreLiveToday";
import { MORE_TOP, MORE_SECTIONS, MORE_SINGLES, MORE_GROWNUPS, MORE_FOOTER, visibleFor } from "@/lib/moreDirectory";
import { useTier } from "@/lib/ageGroup";
import { getBirthCountryCode } from "@/lib/kidProfile";
import { COUNTRIES } from "@/data/countries";
import { track } from "@/lib/track";

const ROW = "flex items-center gap-3 min-h-[52px] px-4 py-2 hover:bg-[var(--color-neutral-surface)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/70 focus-visible:ring-inset";

function Row({ to, emoji, label, testid, onGo, sub, indent }) {
  return (
    <li>
      <NavLink to={to} onClick={onGo} data-testid={testid} className={({ isActive }) => cn(ROW, indent && "pl-8", isActive && "bg-[var(--color-neutral-surface)]")}>
        {emoji ? <span aria-hidden className="text-xl w-7 text-center">{emoji}</span> : null}
        <span className="min-w-0 flex-1"><span className="block font-extrabold text-[var(--color-text)]">{label}</span>{sub && <span className="block mwq-meta">{sub}</span>}</span>
        <span aria-hidden className="text-muted-safe">›</span>
      </NavLink>
    </li>
  );
}

/**
 * MobileDrawer — the universal "More" directory (Oct 2026). Five child hubs stay in the tabbar; More is ONE
 * structured directory over the canonical nav registry: Search · Live Today · Learning · Play & Create ·
 * Discover · My World · Surprise Me · Everything · age/country view · Grown-up Mode · Help · Safety.
 */
export default function MobileDrawer({ open, setOpen }) {
  const tier = useTier();
  const [birth, setBirth] = React.useState(() => getBirthCountryCode());
  React.useEffect(() => {
    if (!open) return undefined;
    setBirth(getBirthCountryCode());
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); document.querySelector('[data-testid="mobile-menu-btn"]')?.focus(); } };
    const onApplied = () => setBirth(getBirthCountryCode());
    window.addEventListener("keydown", onKey);
    window.addEventListener("we-personalize-applied", onApplied);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("we-personalize-applied", onApplied); };
  }, [open, setOpen]);
  if (!open) return null;
  const go = (id) => () => { setOpen(false); track("nav_destination_selected", { hub: id, via: "more" }); };
  const country = birth ? COUNTRIES.find((c) => c.code === birth) : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.16 }}
      style={{ maxHeight: "calc(100dvh - 3.5rem)" }}
      className="lg:hidden border-t-4 border-slate-900 bg-yellow-50 overflow-y-auto overscroll-contain"
      data-testid="mobile-drawer" role="dialog" aria-modal="false" aria-label="More menu"
    >
      <div className="sticky top-0 z-10 bg-yellow-50/95 backdrop-blur border-b-2 border-slate-900 px-3 py-2 flex items-center justify-between" data-testid="mobile-drawer-head">
        <h2 className="font-heading font-black text-lg">More</h2>
        <button type="button" onClick={() => setOpen(false)} data-testid="mobile-drawer-close" aria-label="Close menu"
          className="inline-flex items-center gap-1 min-h-[44px] px-3 rounded-full border-[3px] border-slate-900 bg-white font-bold text-sm">
          <X size={16} strokeWidth={3} /> Close
        </button>
      </div>

      <div className="max-w-3xl mx-auto px-3 py-3 space-y-3" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 88px)" }}>
        <ul className="mwq-card p-0 overflow-hidden" data-testid="more-top">
          {MORE_TOP.map((i) => <Row key={i.id} to={i.to} emoji={i.emoji} label={i.label} testid={`more-${i.id}`} onGo={go(i.id)} sub={i.id === "travel" ? "Games, stories & puzzles for the journey — works offline" : "Lessons, games, countries, stories…"} />)}
        </ul>
        <MoreLiveToday onNavigate={() => setOpen(false)} />

        <nav aria-label="Directory" className="space-y-3" data-testid="more-directory">
          {MORE_SECTIONS.map((s) => {
            const items = visibleFor(s.items, tier);
            if (!items.length) return null;
            return (
              <section key={s.id} className="mwq-card p-0 overflow-hidden" data-testid={`more-section-${s.id}`} aria-labelledby={`more-h-${s.id}`}>
                <h3 id={`more-h-${s.id}`} className="px-4 pt-3 pb-1 text-[11px] font-black uppercase tracking-widest text-slate-700"><span aria-hidden>{s.emoji} </span>{s.title}</h3>
                <ul className="divide-y divide-[var(--color-border)]">
                  {items.map((i) => <Row key={i.id} to={i.to} label={i.label} testid={`more-${i.id}`} onGo={go(i.id)} indent />)}
                </ul>
              </section>
            );
          })}
          <ul className="mwq-card p-0 overflow-hidden divide-y divide-[var(--color-border)]" data-testid="more-singles">
            {MORE_SINGLES.map((i) => <Row key={i.id} to={i.to} emoji={i.emoji} label={i.label} testid={`more-${i.id}`} onGo={go(i.id)} />)}
          </ul>
        </nav>

        <hr className="border-t-2 border-dashed border-slate-400" aria-hidden />

        <AgeBandPicker testid="more-age" />
        <div className="mwq-card p-3 flex items-center justify-between gap-2" data-testid="more-country">
          <p className="font-extrabold text-[var(--color-text)]"><span aria-hidden>🏠 </span>{country ? `Country: ${country.name}` : "Country: not set"}</p>
          <button type="button" onClick={() => { setOpen(false); window.dispatchEvent(new Event("we-open-born-in")); }} data-testid="more-change-country"
            className="min-h-[44px] px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-sm whitespace-nowrap">Change Country</button>
        </div>

        <ul className="mwq-card p-0 overflow-hidden divide-y divide-[var(--color-border)] border-dashed" data-testid="more-grownups">
          <Row to={MORE_GROWNUPS.to} emoji={MORE_GROWNUPS.emoji} label={MORE_GROWNUPS.label} sub="Parents · Teachers · Homeschool · Tutor · Schools" testid="more-grown-ups" onGo={go("grown-ups")} />
          {MORE_FOOTER.map((i) => <Row key={i.id} to={i.to} emoji={i.emoji} label={i.label} testid={`more-${i.id}`} onGo={go(i.id)} />)}
        </ul>

        <div className="flex items-center gap-2 flex-wrap" data-testid="mobile-header-actions">
          <div className="flex-1 min-w-[140px]"><LanguageSwitcher /></div>
          <ReadModeToggle variant="compact" />
          <button type="button" data-testid="mobile-drawer-a11y" onClick={() => { setOpen(false); window.dispatchEvent(new Event("mwq:a11y-open")); }}
            className="min-h-[44px] px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-sm">♿ Make this work for me</button>
        </div>
        <p className="mwq-meta text-center">Shortcut: <Link to="/contact" onClick={go("contact")} data-testid="more-contact" className="underline">Contact</Link> · <Link to="/about" onClick={go("about")} data-testid="more-about" className="underline">About</Link></p>
      </div>
    </motion.div>
  );
}
